import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useApi, errMsg, inr, fmtDate } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Applications, Partners, Leads } from "./AdminCrm";
import { Receipts, Payouts } from "./AdminFinance";
import { Tickets, Content, PlansAdmin, SeoAdmin, SettingsAdmin, UsersAdmin, AuditAdmin } from "./AdminSystem";

const COLORS = { new: "bg-sky-50 text-sky-800 border-sky-200", approved: "bg-emerald-50 text-emerald-800 border-emerald-200", verified: "bg-emerald-50 text-emerald-800 border-emerald-200", active: "bg-emerald-50 text-emerald-800 border-emerald-200", resolved: "bg-emerald-50 text-emerald-800 border-emerald-200", closed_won: "bg-emerald-50 text-emerald-800 border-emerald-200", duplicate: "bg-amber-50 text-amber-800 border-amber-200", rejected: "bg-red-50 text-red-700 border-red-200", invalid: "bg-red-50 text-red-700 border-red-200", reversal: "bg-red-50 text-red-700 border-red-200", open: "bg-amber-50 text-amber-800 border-amber-200" };
export function StatusPill({ s }) {
  const { tc } = useT();
  return <span className={`pill ${COLORS[s] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{tc(s)}</span>;
}
export function Code({ v }) {
  const { tc } = useT();
  return v == null || v === "" ? "—" : tc(v);
}
export function Tx({ v }) {
  const { tl } = useT();
  return v ? tl(v) : "—";
}
export function Opts({ list }) {
  const { tc } = useT();
  return list.map((x) => <option key={x} value={x}>{tc(x)}</option>);
}

export function DataTable({ rows, cols, testid, onRow, empty = "Nothing here yet." }) {
  const { tl } = useT();
  if (!rows?.length) return <p className="text-sm text-slate-600 py-4" data-testid={`${testid}-empty`}>{tl(empty)}</p>;
  return (
    <div className="overflow-x-auto bg-white border rounded-xl">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-slate-50 text-left"><tr>{cols.map(([k, l]) => <th key={k + l} className="p-3 font-semibold whitespace-nowrap">{tl(l)}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.id || r.user_id || i} className={`border-t align-top ${onRow ? "cursor-pointer hover:bg-amber-50/50" : ""}`} onClick={() => onRow?.(r)} data-testid={`${testid}-row-${i}`}>
            {cols.map(([k, l, f]) => <td key={k + l} className="p-3">{f ? f(r[k], r) : String(r[k] ?? "—")}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function ExportBtn({ name }) {
  const api = useApi();
  const { tl } = useT();
  const go = async () => {
    try {
      const r = await api.get(`/admin/export/${name}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${name}.csv`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <button onClick={go} className="inline-flex items-center gap-1 text-sm border rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50" data-testid={`export-${name}`} aria-label={tl("Download CSV")}><Download className="h-4 w-4" />CSV</button>;
}

function Stat({ label, value, testid }) {
  const { tl } = useT();
  return <div className="bg-white border rounded-xl p-4"><p className="text-xs text-slate-500 uppercase tracking-wide">{tl(label)}</p><p className="font-mono text-2xl font-bold mt-1" data-testid={testid}>{value ?? "—"}</p></div>;
}

function Overview() {
  const api = useApi();
  const { tl } = useT();
  const [s, setS] = useState(null);
  const [n, setN] = useState([]);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); api.get("/admin/notifications").then((r) => setN(r.data)); }, [api]);
  if (!s) return null;
  const sum = (arr) => arr.reduce((a, x) => a + x.n, 0);
  const by = (arr, k) => arr.find((x) => x._id === k)?.n || 0;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Applications" value={sum(s.applications)} testid="stat-applications" />
        <Stat label="Approved partners" value={sum(s.partners)} testid="stat-partners" />
        <Stat label="Leads" value={sum(s.leads)} testid="stat-leads" />
        <Stat label="Avg response (h)" value={s.avg_response_hours} testid="stat-response" />
        <Stat label="Won projects" value={by(s.leads, "closed_won")} testid="stat-won" />
        <Stat label="Verified ERCR" value={inr(s.verified_ercr)} testid="stat-ercr" />
        <Stat label="Commission (net, all states, simulation)" value={inr(s.commission_net)} testid="stat-commission" />
        <Stat label="Open grievances / tickets" value={`${s.grievances_open} / ${s.tickets_open}`} testid="stat-open" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div><h3 className="font-semibold mb-2">{tl("Leads by source")}</h3><DataTable testid="lead-sources" rows={s.lead_sources} cols={[["_id", "Source", (v) => <Code v={v} />], ["n", "Count"]]} /></div>
        <div><h3 className="font-semibold mb-2">{tl("Recent notifications")}</h3><DataTable testid="staff-notifications" rows={n.slice(0, 8)} cols={[["created_at", "When", fmtDate], ["title", "Title", (v) => <Tx v={v} />], ["body", "Details", (v) => <Tx v={v} />]]} /></div>
      </div>
    </div>
  );
}

const TABS = [
  ["overview", "Overview", ["admin", "finance", "operations", "content"], Overview],
  ["applications", "Applications", ["admin", "operations"], Applications],
  ["partners", "Partners", ["admin", "operations", "finance"], Partners],
  ["leads", "Leads & projects", ["admin", "operations", "finance"], Leads],
  ["finance", "Receipts & commission", ["admin", "finance"], Receipts],
  ["payouts", "Payouts", ["admin", "finance"], Payouts],
  ["tickets", "Tickets & grievances", ["admin", "operations"], Tickets],
  ["content", "Content", ["admin", "content"], Content],
  ["seo", "SEO & redirects", ["admin", "content"], SeoAdmin],
  ["plans", "Plans", ["admin"], PlansAdmin],
  ["settings", "Settings", ["admin"], SettingsAdmin],
  ["users", "Users & roles", ["admin"], UsersAdmin],
  ["audit", "Audit log", ["admin"], AuditAdmin],
];

export default function AdminDashboard({ me }) {
  const [sp, setSp] = useSearchParams();
  const { tl } = useT();
  const tabs = TABS.filter((t) => t[2].includes(me.role));
  const tab = tabs.find((t) => t[0] === sp.get("tab")) ? sp.get("tab") : "overview";
  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold mb-4">{tl("Admin dashboard")}</h1>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="flex flex-wrap h-auto justify-start">{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`atab-${k}`}>{tl(l)}</TabsTrigger>)}</TabsList>
        {tabs.map(([k, , , C]) => <TabsContent key={k} value={k} className="mt-6">{tab === k && <C me={me} />}</TabsContent>)}
      </Tabs>
    </div>
  );
}
